import React from 'react';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, CalendarRange, Target } from 'lucide-react';
import SheetChart from './SheetChart';

const formatValue = (value) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(Number(value) || 0);

const SheetAnalyticsResult = ({ result, primaryColor = '#008080', sourceLabel = '', onSelect, selectedValue = '', showTimeStatus = false }) => {
  const analyticColor = result?.visual?.colorScheme === 'custom' ? result.visual.customColor : primaryColor;
  if (result?.error) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <div className="flex items-center gap-2 font-semibold"><AlertTriangle size={16} />{result.title}</div>
        <p className="mt-1">{result.error}</p>
      </div>
    );
  }
  if (result?.type === 'chart') {
    return (
      <article className="h-full rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3"><div><h3 className="font-semibold text-slate-900">{result.title}</h3>{sourceLabel && <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">{sourceLabel}</p>}</div>{showTimeStatus && !result?.dateRange?.headerId && <span title="This visual has no date column" className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500"><CalendarRange size={11} />All time</span>}</div>
        {result.description && <p className="mt-1 text-xs text-slate-500">{result.description}</p>}
        <div className="mt-3"><SheetChart result={result} primaryColor={analyticColor} onSelect={onSelect} selectedValue={selectedValue} /></div>
      </article>
    );
  }
  const deltaPositive = Number(result?.delta) >= 0;
  return (
    <article className="h-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" style={{ borderTopColor: analyticColor, borderTopWidth: 3 }}>
      {sourceLabel && <p className="mb-2 truncate text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">{sourceLabel}</p>}
      {showTimeStatus && !result?.dateRange?.headerId && <span title="This metric has no date column" className="float-right inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500"><CalendarRange size={11} />All time</span>}
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{result?.title}</p>
      <p className="mt-2 text-3xl font-bold text-slate-900">{formatValue(result?.value)}</p>
      {result?.description && <p className="mt-1 text-xs text-slate-500">{result.description}</p>}
      {result?.delta !== undefined && (
        <p className={`mt-3 flex items-center gap-1 text-xs font-semibold ${deltaPositive ? 'text-emerald-700' : 'text-rose-700'}`}>
          {deltaPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {formatValue(Math.abs(result.delta))}{result.deltaPercent !== null && ` (${formatValue(Math.abs(result.deltaPercent))}%)`} vs comparison
        </p>
      )}
      {result?.target?.enabled && (
        <div className="mt-3 text-xs text-slate-600">
          <div className="mb-1 flex items-center justify-between"><span className="flex items-center gap-1"><Target size={13} />Target {formatValue(result.target.value)}</span><span>{result.targetMet ? 'Met' : 'In progress'}</span></div>
          {result.targetProgress !== null && <div className="h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full" style={{ width: `${result.targetProgress}%`, backgroundColor: analyticColor }} /></div>}
        </div>
      )}
    </article>
  );
};

export default SheetAnalyticsResult;
